import { plateFont, numFont } from '../../../utils/fonts';
import React, { useState } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { statusConfig } from '../../shared/StatusBadge';
import { useWidgetFrame } from '../frameContext';
import { bodyHeightPx } from '../sizes';
import { avatarColor } from './format';

const LANE_H = 40;
const OWN_LANE_H = 64;
const NAME_W = 164;
const NAME_W_MOBILE = 44;

/**
 * Per-employee day timeline (design template 5): hour scale, lanes with avatar, blocks with
 * plate + work number, hatched absences and the red NYT line.
 *
 * lanes: [{ id, initials, name, caption, colorIdx }]
 * blocks: [{ laneId, start, end, plate, workNumber, state, onClick }] (decimal hours)
 * absences: [{ laneId, start, end, label }]
 */
export default function TimelineBody({ lanes = [], dayStart = 7, dayEnd = 17, blocks = [], absences = [], now = null, own = false, maxLanes = 5 }) {
  const { t } = useTranslation();
  const frame = useWidgetFrame();
  const mobile = Boolean(frame?.isMobile);
  const [expanded, setExpanded] = useState(false);
  const laneH = own ? OWN_LANE_H : LANE_H;
  const SCALE_H = 26;
  const FOOTER_H = 40;
  let laneLimit = own ? 1 : mobile ? 3 : Math.min(maxLanes, Math.max(1, Math.floor((bodyHeightPx(frame?.size || 'L') - SCALE_H) / laneH)));
  if (!own && !mobile && lanes.length > laneLimit) {
    laneLimit = Math.max(1, Math.floor((bodyHeightPx(frame?.size || 'L') - SCALE_H - FOOTER_H) / laneH));
  }
  const shown = expanded ? lanes : lanes.slice(0, laneLimit);
  const hiddenCount = lanes.length - shown.length;
  const span = Math.max(1, dayEnd - dayStart);
  const pct = (hour) => `${Math.max(0, Math.min(100, ((hour - dayStart) / span) * 100))}%`;
  const hours = [];
  for (let h = Math.ceil(dayStart); h <= Math.floor(dayEnd); h += 1) hours.push(h);
  const nameW = mobile ? NAME_W_MOBILE : NAME_W;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexShrink: 0, height: SCALE_H, borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ width: nameW, flexShrink: 0 }} />
        <Box sx={{ flex: 1, position: 'relative' }}>
          {hours.map((hour, index) => (
            <Typography
              key={hour}
              sx={{ position: 'absolute', left: pct(hour), top: 6, transform: 'translateX(-50%)', ...numFont, fontSize: 10.5, color: 'text.secondary', display: mobile && index % 2 ? 'none' : 'block' }}
            >
              {String(hour).padStart(2, '0')}:00
            </Typography>
          ))}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: expanded ? 'auto' : 'hidden', position: 'relative' }}>
        {shown.map((lane, laneIndex) => (
          <Box key={lane.id} sx={{ display: 'flex', height: laneH, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ width: nameW, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, px: mobile ? 1 : 1.5, minWidth: 0 }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: lane.color || avatarColor(lane.colorIdx ?? laneIndex) }}>{lane.initials}</Avatar>
              {!mobile ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                    {lane.name}
                  </Typography>
                  {lane.caption ? (
                    <Typography noWrap sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                      {lane.caption}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Box>
            <Box sx={{ flex: 1, position: 'relative', backgroundImage: `repeating-linear-gradient(90deg, transparent 0, transparent calc(${100 / span}% - 1px), #f1f5f9 calc(${100 / span}% - 1px), #f1f5f9 ${100 / span}%)` }}>
              {absences
                .filter((absence) => absence.laneId === lane.id)
                .map((absence, index) => (
                  <Box
                    key={`a${index}`}
                    title={absence.label}
                    sx={{ position: 'absolute', top: 6, bottom: 6, left: pct(absence.start), width: `calc(${pct(absence.end)} - ${pct(absence.start)})`, borderRadius: '6px', backgroundImage: 'repeating-linear-gradient(135deg,#f1f5f9 0 6px,#e2e8f0 6px 12px)' }}
                  />
                ))}
              {blocks
                .filter((block) => block.laneId === lane.id)
                .map((block, index) => {
                  const state = statusConfig[block.state] || statusConfig.planned;
                  return (
                    <Box
                      key={`b${index}`}
                      onClick={block.onClick && frame?.mode !== 'edit' ? block.onClick : undefined}
                      title={`${block.plate || ''} ${block.workNumber ? `#${block.workNumber}` : ''}`.trim()}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        bottom: 6,
                        left: pct(block.start),
                        width: `max(6px, calc(${pct(block.end)} - ${pct(block.start)}))`,
                        borderRadius: '6px',
                        bgcolor: state.bg,
                        borderLeft: `3px solid ${state.color}`,
                        px: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        overflow: 'hidden',
                        cursor: block.onClick ? 'pointer' : 'default',
                        ...plateFont,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: '#1a365d' }}>
                        {block.plate}
                      </Box>
                      {block.workNumber ? (
                        <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                          #{block.workNumber}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
            </Box>
          </Box>
        ))}
        {now != null && now >= dayStart && now <= dayEnd ? (
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `calc(${nameW}px + (100% - ${nameW}px) * ${(now - dayStart) / span})`, width: 2, bgcolor: '#c53030', pointerEvents: 'none' }}>
            <Typography sx={{ position: 'absolute', top: 2, left: 4, ...numFont, fontSize: 10, fontWeight: 700, color: '#c53030' }}>
              {t('dashboard.chips.now')}
            </Typography>
          </Box>
        ) : null}
      </Box>
      {hiddenCount > 0 || expanded ? (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((value) => !value)}
          sx={{ height: 40, flexShrink: 0, border: 0, borderTop: '1px solid #f1f5f9', bgcolor: 'transparent', color: 'secondary.main', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
        >
          {expanded ? t('dashboard.actions.showLess') : t('dashboard.actions.showMore', { n: hiddenCount })}
        </Box>
      ) : null}
    </Box>
  );
}
